import { useState, useEffect } from "react";
import { X, Mail, Gift, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUBSCRIBED_KEY = "mathallen_newsletter_subscribed";

export default function NewsletterPopup() {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    // Check if already subscribed (permanently stored)
    const alreadySubscribed = localStorage.getItem(SUBSCRIBED_KEY);
    if (alreadySubscribed) return;

    // Show popup after 2 seconds on every page load
    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    // Just close - will show again on next page refresh
    setIsOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Ange en giltig e-postadress");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      setSubscribed(true);
      
      // Mark as subscribed permanently - won't show again
      localStorage.setItem(SUBSCRIBED_KEY, "true");
      
      toast.success("Tack för din prenumeration!");
      
      // Close after showing success
      setTimeout(() => {
        setIsOpen(false);
      }, 3000);
    } catch (error) {
      if (error.response?.status === 400) {
        // Already subscribed - also mark as subscribed
        localStorage.setItem(SUBSCRIBED_KEY, "true");
        toast.info("Du är redan prenumerant!");
        setTimeout(() => {
          setIsOpen(false);
        }, 2000);
      } else {
        toast.error("Något gick fel. Försök igen.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[9998] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={handleClose}
      data-testid="newsletter-popup-overlay"
    >
      <div 
        className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-scale-in"
        onClick={(e) => e.stopPropagation()}
        data-testid="newsletter-popup-container"
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 w-8 h-8 bg-stone-100 hover:bg-stone-200 rounded-full flex items-center justify-center transition-colors"
          data-testid="newsletter-popup-close"
          aria-label="Stäng"
        >
          <X className="w-4 h-4 text-stone-600" />
        </button>

        {/* Header with gradient */}
        <div className="bg-gradient-to-br from-red-600 to-red-700 px-6 py-8 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Gift className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">
            Missa inte våra erbjudanden!
          </h2>
          <p className="text-white/90 text-sm">
            Prenumerera på vårt nyhetsbrev och få veckans bästa deals direkt i din inbox
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          {!subscribed ? (
            <>
              {/* Benefits */}
              <div className="space-y-3 mb-6">
                {[
                  "Exklusiva erbjudanden varje vecka",
                  "Först med att veta om kampanjer",
                  "Tips och recept från våra kockar"
                ].map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-green-600" />
                    </div>
                    <span className="text-stone-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <Input
                    type="email"
                    placeholder="Din e-postadress"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 py-6 text-base border-stone-200 focus:border-red-500 focus:ring-red-500 rounded-xl"
                    data-testid="newsletter-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full py-6 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl text-base"
                  data-testid="newsletter-submit-button"
                >
                  {loading ? "Skickar..." : "Prenumerera nu"}
                </Button>
              </form>

              <p className="text-center text-stone-400 text-xs mt-4">
                Genom att prenumerera godkänner du våra villkor. Avsluta när som helst.
              </p>
            </>
          ) : (
            /* Success State */
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-2">
                Välkommen!
              </h3>
              <p className="text-stone-600">
                Du är nu prenumerant och kommer få våra bästa erbjudanden direkt i din inbox.
              </p>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes scale-in {
          from { 
            opacity: 0; 
            transform: scale(0.9) translateY(20px); 
          }
          to { 
            opacity: 1; 
            transform: scale(1) translateY(0); 
          }
        }
        .animate-scale-in {
          animation: scale-in 0.4s ease-out;
        }
      `}</style>
    </div>
  );
}
